import moment from 'moment';

import { IFiveWResponse } from 'common/api/type-def';
import { ENTITY_TYPE } from 'common/constants/TaxonomyConstants';

import {
	IHorizonBusinessUnits,
	IProductItem,
} from 'features/HorizonScanning/api/type-def';

import { IEntityDetailResponse, ITemplateType } from '../api/type-def';

import grammrCheckStyles from './MappingDetail/Overview/GrammarCheck.module.scss';
import {
	entityControlDetailsFormat,
	entityDetailsMappingObj,
	entityObligationDetailsFormat,
	entityPolicyDetailsFormat,
	entityProcessDetailsFormat,
	entityRiskDetailsFormat,
} from './MappingStaticData';

import styles from './RelationMapping.module.scss';

const {
	mappintUtilsDataRed,
	mappingUtilsDataBlue,
	mappingUtilsDataYellow,
	mappingUtilsdataBlack,
	process,
	risk,
	policy,
	control,
	obligation,
	add,
} = styles;
const {
	validationwhat,
	validationwhen,
	validationwhy,
	validationwhere,
	validationwho,
} = grammrCheckStyles;
const { PROCESS, RISK, POLICY, CONTROL, GROUP, OBLIGATION } = ENTITY_TYPE;

export const mappingFilters = [
	{ label: 'All Mapped', value: 'all' },
	{ label: 'Impacted', value: 'impacted' },
	{ label: 'Not Impacted', value: 'not impacted' },
	{ label: 'Unmapped, Maybe Impacted', value: 'pending review' },
];

export const risklevel = [
	{
		label: 'High',
		value: 'High',
	},
	{
		label: 'Medium',
		value: 'Medium',
	},
	{
		label: 'Low',
		value: 'Low',
	},
];

export const taskStatus = [
	{
		label: 'In Progress',
		value: 'In Progress',
	},
	{
		label: 'Not Started',
		value: 'Not Started',
	},
	{
		label: 'Complete',
		value: 'Complete',
	},
	{
		label: 'Ready for Review',
		value: 'Ready for Review',
	},
];

export interface IControlData {
	label: string;
	value: string | null;
}

export enum entityAssociatedData {
	OPEN_ISSUES = 'Open Issues',
	ASSOCIATED_PRODUCTS = 'Associated Products',
	OPEN_TASKS = 'Open Tasks',
}

export const countColourMapping = (label: string) => {
	switch (label) {
		case entityAssociatedData.OPEN_ISSUES:
			return mappintUtilsDataRed;
		case entityAssociatedData.ASSOCIATED_PRODUCTS:
			return mappingUtilsDataBlue;
		case entityAssociatedData.OPEN_TASKS:
			return mappingUtilsDataYellow;
		default:
			return mappingUtilsdataBlack;
	}
};

export const fiveWValidationResponse = (data: IFiveWResponse) => {
	const alpha = 35;
	const fiveWValidation = [
		{
			id: 1,
			name: 'What',
			color: validationwhat,
			terms: data?.fiveWResponse.What || [],
		},
		{
			id: 2,
			name: 'Where',
			color: validationwhere,
			terms: data?.fiveWResponse.Where || [],
		},
		{
			id: 3,
			name: 'Who',
			color: validationwho,
			terms: data?.fiveWResponse.Who || [],
		},
		{
			id: 4,
			name: 'When',
			color: validationwhen,
			terms: data?.fiveWResponse.When || [],
		},
		{
			id: 5,
			name: 'Why',
			color: validationwhy,
			terms: data?.fiveWResponse.Why || [],
		},
	];

	const missingControlValidations: { word: string; color: string }[] = [];
	const availableControlValidation: { word: string; color: string }[] = [];
	let missingValuesString = '';

	fiveWValidation.forEach((res) => {
		if (res.terms.length > 0) {
			let found = false;
			res.terms.forEach((element: string) => {
				if (
					data.controlDescription.toLowerCase().includes(element.toLowerCase())
				) {
					availableControlValidation.push({
						word: element,
						color: `${res.color}${alpha}`,
					});
					found = true;
				}
			});

			if (!found) {
				missingControlValidations.push({
					word: res.name,
					color: `${res.color}${alpha}`,
				});
			}
		} else {
			missingControlValidations.push({
				word: res.name,
				color: `${res.color}${alpha}`,
			});
		}
	});

	const missingControlValidationsSet = missingControlValidations.filter(
		(value, index, array) =>
			index === array.findIndex((item) => item.word === value.word),
	);

	missingControlValidationsSet.forEach((res) => {
		missingValuesString += `${res.word} `;
	});

	return {
		fiveWValidation,
		availableControlValidation,
		missingValuesString,
		missingControlValidations,
	};
};

export const getRelationshipCardBorderColor = (type: string) => {
	switch (type) {
		case PROCESS:
			return process;
		case RISK:
			return risk;
		case POLICY:
			return policy;
		case CONTROL:
			return control;
		case GROUP:
			return obligation;
		default:
			return add;
	}
};

export const getBusinessUnitValue = (responseBody: IEntityDetailResponse) => {
	const businessUnitsToDisplay = responseBody?.businessUnits.map(
		(el: IHorizonBusinessUnits) => el.businessUnitName,
	);
	return businessUnitsToDisplay ? businessUnitsToDisplay.join(',') : null;
};

export const getProductsValue = (responseBody: IEntityDetailResponse) =>
	responseBody?.products.map((res: IProductItem) => res && res.productName);

export const getRiskLevelValue = (responseBody: IEntityDetailResponse) => {
	const { riskLevel1, riskLevel2, riskLevel3 } = responseBody;
	const riskLevelsMerged = [riskLevel1, riskLevel2, riskLevel3].filter(Boolean);
	return riskLevelsMerged.length > 0 ? riskLevelsMerged.join(', ') : 'NA';
};

export const getDueDateValue = (responseBody: IEntityDetailResponse) => {
	const { reviewByDate } = responseBody;
	return moment(reviewByDate, 'YYYY-MM-DD').format('MM/DD/YYYY');
};

export const getFormattedEntityValue = (
	responseBody: IEntityDetailResponse,
	type: string,
) => {
	let entityVal: ITemplateType[];
	switch (type) {
		case PROCESS:
			entityVal = getFormattedTemplateValue(
				responseBody,
				entityProcessDetailsFormat,
				PROCESS,
			);
			break;
		case CONTROL:
			entityVal = getFormattedTemplateValue(
				responseBody,
				entityControlDetailsFormat,
				CONTROL,
			);
			break;
		case POLICY:
			entityVal = getFormattedTemplateValue(
				responseBody,
				entityPolicyDetailsFormat,
				POLICY,
			);
			break;
		case RISK:
			entityVal = getFormattedTemplateValue(
				responseBody,
				entityRiskDetailsFormat,
				RISK,
			);
			break;
		default:
			entityVal = getFormattedTemplateValue(
				responseBody,
				entityObligationDetailsFormat,
				OBLIGATION,
			);
			break;
	}
	return entityVal;
};

export const getFormattedTemplateValue = (
	responseBody: IEntityDetailResponse,
	format: ITemplateType[],
	type: string,
) => {
	format.forEach((val) => {
		if (
			typeof responseBody === 'object' &&
			Object.keys(responseBody).includes(
				entityDetailsMappingObj?.[type?.toLowerCase()][val.label],
			)
		) {
			if (
				val.label.toLowerCase() === 'Business unit'.toLowerCase() &&
				type === OBLIGATION
			) {
				val.value = getBusinessUnitValue(responseBody);
			} else if (val.label === 'products') {
				val.value = getProductsValue(responseBody);
			} else if (val.label === 'Risk level') {
				val.value = getRiskLevelValue(responseBody);
			} else if (val.label === 'Due date') {
				val.value = getDueDateValue(responseBody);
			} else {
				val.value =
					responseBody[
						entityDetailsMappingObj?.[type.toLowerCase()][val.label]
					];
			}
		}
		return val;
	});

	return format;
};
